import { Flex, Spacer } from "@chakra-ui/react";
import { MdOutlineEdit } from "react-icons/md";
import { FormattedMessage, useIntl } from "react-intl";

import { AddUserIcon, RanaButton, RanaHeading, RanaTable, TrashIcon } from "@nens/ranalib";

import { TableEmptyUserOrInvite } from "@/components/table-empty-user-or-invite";
import { UserInviteList } from "@/features/invite-user";
import { useProjectActionAllowed } from "@/hooks/use-action-allowed-in-project";
import { Profile } from "@/lib/profile";
import { nameColumnString } from "@/translations";
import { getCompleteName } from "@/utils/get-complete-name";

import { ProjectUserEditableRoleModal } from "./components/project-user-editable-role-modal";
import { ProjectUserInviteModal } from "./components/project-user-invite-modal";
import { ProjectUserRemoveModal } from "./components/project-user-remove-modal";
import { useProjectUserList } from "./hooks/use-project-user-list";

export function ProjectUserList() {
  const { canManageProject } = useProjectActionAllowed();
  const intl = useIntl();
  const {
    tenantName,
    usersList,
    usersListQuery,
    openModal,
    searchString,
    setSearchString,
    changeUsersRole,
    inviteUser,
    removeUsers,
    closeModal,
  } = useProjectUserList();
  const canEditUsers = canManageProject;
  const inviteUsersClick = canEditUsers ? inviteUser : undefined;
  return (
    <>
      {openModal?.type === "invite" ? <ProjectUserInviteModal onClose={closeModal} /> : null}
      {openModal?.type === "change-role" ? (
        <ProjectUserEditableRoleModal users={openModal.users} onClose={closeModal} />
      ) : null}
      {openModal?.type === "remove" ? (
        <ProjectUserRemoveModal onClose={closeModal} users={openModal.users} />
      ) : null}
      <Flex flexWrap="wrap" gap={2} alignItems="center">
        <RanaHeading type="h4">
          <FormattedMessage id="heading.invited_users" defaultMessage="Invited users" />
        </RanaHeading>
        <Spacer />
        {canManageProject ? (
          <RanaButton onClick={inviteUser} leftIcon={<AddUserIcon />} variant="primary">
            <FormattedMessage id="button.invite" defaultMessage="Invite users" />
          </RanaButton>
        ) : null}
      </Flex>
      <UserInviteList openInviteModal={inviteUsersClick} />

      <RanaHeading type="h4" pb={2}>
        <FormattedMessage id="heading.users" defaultMessage="Users" />
      </RanaHeading>

      <RanaTable
        hasRowActions
        emptyElement={<TableEmptyUserOrInvite onLinkCLick={inviteUsersClick} />}
        isSkeleton={usersListQuery.isLoading}
        columnDefinitions={[
          {
            width: "table_cell_xxxs",
          },
          {
            title: nameColumnString(intl),
            width: "table_cell",
          },
          {
            title: intl.formatMessage({ id: "column.email", defaultMessage: "Email" }),
            width: "table_cell",
          },
          {
            title: intl.formatMessage({
              id: "column.project_role",
              defaultMessage: "Project role",
            }),
            width: "table_cell_xs",
          },
        ]}
        tableDataIds={usersList.map(({ user }) => user.id)}
        tableActions={[
          {
            label: intl.formatMessage({
              id: "menu.change_project_roles",
              defaultMessage: "Change project roles",
            }),
            action: (rowIds) =>
              changeUsersRole(usersList.filter((user) => rowIds.includes(user.user.id))),
            icon: <MdOutlineEdit />,
            isDisabled: !canManageProject,
          },
          {
            label: intl.formatMessage({
              id: "menu.remove_selected_users",
              defaultMessage: "Remove selected users",
            }),
            action: (rowIds) =>
              removeUsers(usersList.filter((user) => rowIds.includes(user.user.id))),
            icon: <TrashIcon />,
            isDisabled: !canManageProject,
          },
        ]}
      >
        <RanaTable.Filters
          search={{
            placeholder: intl.formatMessage({
              id: "placeholder.find_user",
              defaultMessage: "Find user",
            }),
            searchString,
            setSearchString,
          }}
        />
        <RanaTable.Container>
          <RanaTable.Header />
          <RanaTable.Body>
            {usersList.map((user) => (
              <RanaTable.Row
                key={user.user.id + ""}
                rowId={user.user.id + ""}
                rowActions={[
                  ...(canManageProject
                    ? [
                        {
                          label: intl.formatMessage({
                            id: "menu.change_project_role",
                            defaultMessage: "Change project role",
                          }),
                          action: () => changeUsersRole([user]),
                          icon: <MdOutlineEdit />,
                        },
                        {
                          label: intl.formatMessage({
                            id: "menu.remove_user",
                            defaultMessage: "Remove user",
                          }),
                          action: () => removeUsers([user]),
                          icon: <TrashIcon />,
                        },
                      ]
                    : []),
                ]}
              >
                <RanaTable.Cell>
                  <Profile inTable user={user.user} />
                </RanaTable.Cell>
                <RanaTable.Cell>
                  {getCompleteName(user.user) === tenantName
                    ? intl.formatMessage({ id: "table.you", defaultMessage: "You" })
                    : getCompleteName(user.user)}
                </RanaTable.Cell>
                <RanaTable.Cell>{user.user.email}</RanaTable.Cell>
                <RanaTable.Cell>{user.role}</RanaTable.Cell>
              </RanaTable.Row>
            ))}
          </RanaTable.Body>
        </RanaTable.Container>
      </RanaTable>
    </>
  );
}
